import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { obtenerCotizaciones, obtenerCotizacion } from "../services/cotizacionService";
import PageHeader from "../components/PageHeader";
import TableToolbar from "../components/TableToolbar";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";

const currentYear = new Date().getFullYear();
const yearsOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function getClavUsr() {
  try {
    const raw = JSON.parse(localStorage.getItem("user") || "null");
    const u = raw?.user ?? raw ?? {};
    const verTodas = u?.verTodasCotizaciones ?? u?.VerTodasCotizaciones ?? false;
    if (verTodas) return null; // sin filtro → ve todas
    return u?.clave ?? u?.Clave ?? null;
  } catch {
    return null;
  }
}

export default function CotizacionesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [importes, setImportes] = useState({ mxn: 0, usd: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState(
    () => sessionStorage.getItem("cot_search") ?? ""
  );
  const [filtroFacturar, setFiltroFacturar] = useState(() => {
    const v = sessionStorage.getItem("cot_filtro");
    return ["TODAS", "FACTURADAS", "PENDIENTES", "SOLO_COTIZADAS"].includes(v) ? v : "TODAS";
  });
  const [anio, setAnio] = useState(
    () => sessionStorage.getItem("cot_anio") ?? String(currentYear)
  );
  const [mes, setMes] = useState(
    () => sessionStorage.getItem("cot_mes") ?? String(new Date().getMonth() + 1)
  );
  const [loading, setLoading] = useState(false);
  const { toasts, closeToast, error: toastError } = useToast();

  // Modal de vista rápida (ver renglones sin entrar a editar)
  const [detalleModal, setDetalleModal] = useState(null); // { id, cotizacion, ... }
  const [detalleLoading, setDetalleLoading] = useState(false);

  const abrirDetalle = async (id) => {
    setDetalleModal({ id });
    setDetalleLoading(true);
    try {
      const { data } = await obtenerCotizacion(id);
      setDetalleModal(data);
    } catch {
      toastError("No se pudo cargar el detalle de la cotización.");
      setDetalleModal(null);
    } finally {
      setDetalleLoading(false);
    }
  };

  const clavUsr = useMemo(() => getClavUsr(), []);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    obtenerCotizaciones(page, pageSize, search, filtroFacturar, clavUsr, anio, mes)
      .then(({ data }) => {
        if (cancel) return;
        const items = (data?.data ?? []).map((r) => ({
          ...r,
          fechaFmt: r.fecha ? new Date(r.fecha).toLocaleDateString("es-MX") : "",
          importeFmt: typeof r.total === "number" ? r.total.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "",
          monedaFmt: r.moneda === "P" ? "MXN" : r.moneda === "D" ? "USD" : (r.moneda ?? ""),
          facturada: r.facturada === true,
          onEditar: () => navigate(`/cotizaciones/${r.id}/editar`),
        }));
        setRows(items);
        setTotal(data?.total ?? 0);
        setImportes({ mxn: data?.totalImporteMxn ?? 0, usd: data?.totalImporteUsd ?? 0 });
      })
      .catch((e) => { toastError(e?.message || "No se pudieron cargar las cotizaciones"); })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [page, pageSize, search, filtroFacturar, anio, mes, clavUsr, navigate]);

  const filteredRows = useMemo(() => {
    if (filtroFacturar === "TODAS") return rows;
    if (filtroFacturar === "FACTURADAS") return rows.filter((r) => r.facturada);
    if (filtroFacturar === "PENDIENTES") return rows.filter((r) => !r.facturada && r.porFacturar);
    if (filtroFacturar === "SOLO_COTIZADAS") return rows.filter((r) => !r.facturada && !r.porFacturar);
    return rows;
  }, [rows, filtroFacturar]);

  const columns = [
    {
      key: "cotizacion", title: "# Cotizacion", style: { width: 140 },
      render: (r) => (
        <button
          onClick={() => abrirDetalle(r.id)}
          title="Ver renglones de la cotización"
          style={{
            background: "none", border: "none", cursor: "pointer", padding: 0,
            color: "var(--clr-primary)", fontWeight: "var(--fw-semibold)",
            fontFamily: "var(--font-mono)", fontSize: "var(--fs-sm)",
          }}
        >
          {r.cotizacion}
        </button>
      ),
    },
    { key: "fechaFmt", title: "Fecha", style: { width: 120 } },
    { key: "clienteNombre", title: "Cliente" },
    {
      key: "observaciones", title: "Observaciones", style: { maxWidth: 220 },
      render: (r) => (
        <span
          title={r.observaciones || ""}
          style={{
            display: "block", overflow: "hidden", textOverflow: "ellipsis",
            whiteSpace: "nowrap", maxWidth: 220,
          }}
        >
          {r.observaciones || ""}
        </span>
      ),
    },
    {
      key: "importeFmt", title: "Importe",
      className: "num", style: { width: 150 },
    },
    { key: "monedaFmt", title: "Moneda", style: { width: 90 }, className: "center" },
    {
      key: "facturada", title: "Estatus", style: { width: 120 }, className: "center",
      render: (r) => r.facturada
        ? <span className="lmd-badge lmd-badge-success">Facturada</span>
        : r.porFacturar
          ? <span className="lmd-badge lmd-badge-warning">Pendiente</span>
          : <span className="lmd-badge lmd-badge-neutral">Solo cotizada</span>,
    },
    {
      key: "acciones", title: "", style: { width: 90 },
      render: (r) => (
        <button className="lmd-btn lmd-btn-warning lmd-btn-sm" onClick={r.onEditar}>
          Editar
        </button>
      ),
    },
  ];

  const handleFiltroChange = (valor) => {
    sessionStorage.setItem("cot_filtro", valor);
    setFiltroFacturar(valor);
    setPage(1);
  };

  const handleAnioChange = (v) => {
    sessionStorage.setItem("cot_anio", v);
    setAnio(v);
    setPage(1);
  };

  const handleMesChange = (v) => {
    sessionStorage.setItem("cot_mes", v);
    setMes(v);
    setPage(1);
  };

  const extraRight = (
    <>
      <select className="lmd-select" value={anio} onChange={(e) => handleAnioChange(e.target.value)}>
        <option value="TODOS">Todos los años</option>
        {yearsOptions.map((y) => (
          <option key={y} value={String(y)}>{y}</option>
        ))}
      </select>
      <select className="lmd-select" value={mes} onChange={(e) => handleMesChange(e.target.value)}>
        <option value="TODOS">Todos los meses</option>
        {MESES.map((m, i) => (
          <option key={i + 1} value={String(i + 1)}>{m}</option>
        ))}
      </select>
      <select className="lmd-select" value={filtroFacturar} onChange={(e) => handleFiltroChange(e.target.value)}>
        <option value="TODAS">Todas</option>
        <option value="FACTURADAS">Facturadas</option>
        <option value="PENDIENTES">Pendientes</option>
        <option value="SOLO_COTIZADAS">Solo cotizadas</option>
      </select>
    </>
  );

  return (
    <>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader title="Cotizaciones" subtitle="Seguimiento y gestion de cotizaciones" />

      <div className="lmd-card">
        <TableToolbar
          searchPlaceholder="Buscar por # cotizacion o cliente..."
          searchValue={search}
          onSearchChange={(v) => { sessionStorage.setItem("cot_search", v); setSearch(v); }}
          onSearch={() => setPage(1)}
          sizeValue={pageSize}
          onSizeChange={(n) => { setPageSize(n); setPage(1); }}
          onAdd={() => navigate("/cotizaciones/nueva")}
          addText="Nueva Cotizacion"
          extraRight={extraRight}
        />

        {loading && (
          <div className="lmd-loading-bar"><div className="lmd-spinner" /> Cargando...</div>
        )}

        <DataTable columns={columns} rows={filteredRows} keyField="id" />

        <div className="lmd-pagination">
          <span className="lmd-pagination-info">
            Total: <strong>{total}</strong> registros
            {" — Importe: "}
            <strong>
              ${Number(importes.mxn).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN
            </strong>
            {Number(importes.usd) > 0 && (
              <>
                {" + "}
                <strong>
                  ${Number(importes.usd).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                </strong>
              </>
            )}
          </span>
          <div className="lmd-pagination-spacer" />
          <button className="lmd-page-btn" disabled={page <= 1 || loading} onClick={() => setPage(1)}>«</button>
          <button className="lmd-page-btn" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
          <span style={{ padding: "0 10px", fontSize: "var(--fs-sm)", color: "var(--clr-text-secondary)" }}>
            Pag. <strong>{page}</strong> / <strong>{totalPages}</strong>
          </span>
          <button className="lmd-page-btn" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>›</button>
          <button className="lmd-page-btn" disabled={page >= totalPages || loading} onClick={() => setPage(totalPages)}>»</button>
        </div>
      </div>

      {/* ── MODAL VISTA RÁPIDA ── */}
      {detalleModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setDetalleModal(null); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center",
          }}
        >
          <div style={{
            background: "#fff", width: "92%", maxWidth: 800, borderRadius: 8, padding: 24,
            position: "relative", maxHeight: "85vh", overflowY: "auto",
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          }}>
            <button
              onClick={() => setDetalleModal(null)}
              style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "var(--clr-text-secondary)" }}
            >
              ✕
            </button>

            {detalleLoading ? (
              <div className="lmd-loading-bar"><div className="lmd-spinner" /> Cargando...</div>
            ) : (
              <>
                <h3 style={{ margin: "0 0 4px", fontSize: "var(--fs-lg)", fontWeight: "var(--fw-bold)" }}>
                  Cotización #{detalleModal.cotizacion}
                </h3>
                <p style={{ margin: "0 0 4px", fontSize: "var(--fs-sm)", color: "var(--clr-text-secondary)" }}>
                  {detalleModal.nombreCliente || detalleModal.cliente} — {detalleModal.fecha ? new Date(detalleModal.fecha).toLocaleDateString("es-MX") : ""}
                </p>
                {detalleModal.observaciones && (
                  <p style={{ margin: "0 0 16px", fontSize: "var(--fs-sm)" }}>
                    <strong>Observaciones:</strong> {detalleModal.observaciones}
                  </p>
                )}

                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "var(--clr-bg)", borderBottom: "2px solid var(--clr-border)" }}>
                      {["Clave", "Descripción", "Cant.", "Precio", "Importe"].map((h, i) => (
                        <th key={i} style={{
                          padding: "8px 10px", fontSize: "var(--fs-xs)", fontWeight: "var(--fw-semibold)",
                          color: "var(--clr-text-secondary)", textTransform: "uppercase",
                          textAlign: i >= 2 ? "right" : "left",
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(detalleModal.cotizacionesDetalle || []).map((d, i) => (
                      <tr key={d.id ?? i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc", borderBottom: "1px solid var(--clr-border-subtle)" }}>
                        <td style={{ padding: "8px 10px", fontFamily: "var(--font-mono)" }}>{d.articulo}</td>
                        <td style={{ padding: "8px 10px" }}>{d.descripcion}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>{d.cantidad}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "var(--font-mono)" }}>
                          {Number(d.precioUnitario || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "var(--font-mono)" }}>
                          {Number(d.importe || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: "2px solid var(--clr-border)", background: "var(--clr-bg)" }}>
                      <td colSpan={4} style={{ padding: "8px 10px", fontWeight: "var(--fw-semibold)", textAlign: "right", fontSize: "var(--fs-sm)", color: "var(--clr-text-secondary)" }}>
                        Total ({detalleModal.moneda === "D" ? "USD" : "MXN"}):
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: "var(--fw-extrabold)", color: "var(--clr-primary)" }}>
                        {Number(detalleModal.total || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                </table>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
                  <button className="lmd-btn lmd-btn-secondary" onClick={() => setDetalleModal(null)}>Cerrar</button>
                  <button
                    className="lmd-btn lmd-btn-primary"
                    onClick={() => navigate(`/cotizaciones/${detalleModal.id}/editar`)}
                  >
                    Editar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
